import { Space, Table, Tag, Button as AntButton, Input, Typography } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined, SearchOutlined } from "@ant-design/icons";
import React, { useEffect, useState } from "react";
import Button from "../components/Forms/Controls/Button";
import DefaultLayout from "../components/Layouts/DefaultLayout";
import {
  WatchFace,
  WatchFaceType,
  WFComplicationType,
  WFStatus,
} from "../dto/WatchFace.dto";
import {
  useDeleteWatchFace,
  useGetWatchFaces,
} from "../services/api/watchface";
import CreateWatchFaceModal from "../components/Modals/CreateWatchFaceModal";
import Modal from "../components/Modal";
import { useGetCategories } from "../services/api/category";
import { CategoryType } from "../dto/Category.dto";
import { WatchFacePreview } from "../components/WatchFacePreview/WatchFacePreview";
import { useDebounce } from "../hooks/useDebounce";
import { getWatchfacePreviewMedia } from "../utils/getWatchfacePreviewUrl";
import styles from "./WatchFacesPage.module.scss";

const { Text } = Typography;

interface DataType {
  key: string;
  image: { src: string; alt: string; type: "video" | "img" };
  depthUrl: string;
  name: string;
  type: string;
  isFree: boolean;
  isApproved: boolean;
  category: string;
  categoryType: string;
  status: WFStatus;
  tags: string[];
  minAppVersion: number[];

  installsCount: number;
  likesCount: number;
  reportsCount: number;
  position: number;
  ordinalPosition: number;

  styles: any;
}

const watchFaceTypeFilters = [
  {
    text: "Photo",
    value: WatchFaceType.Photo,
  },
  {
    text: "Portrait",
    value: WatchFaceType.Portrait,
  },
  {
    text: "Animated",
    value: WatchFaceType.Animated,
  },
  {
    text: "Parmesan",
    value: WatchFaceType.Parmesan,
  },
  {
    text: "Full Screen",
    value: WatchFaceType.EdgeToEdge,
  },
  {
    text: "Complication",
    value: WatchFaceType.Complication,
    children: [
      {
        text: "Infograph",
        value: WFComplicationType.Infograph,
      },
      {
        text: "California",
        value: WFComplicationType.California,
      },
      {
        text: "Meridian",
        value: WFComplicationType.Meridian,
      },
      {
        text: "Infograph Modular",
        value: WFComplicationType.InfographModular,
      },
      {
        text: "Infograph Modular Colored",
        value: WFComplicationType.InfographModularColored,
      },
      {
        text: "XLarge",
        value: WFComplicationType.XLarge,
      },
      {
        text: "XLarge Colored",
        value: WFComplicationType.XLargeColored,
      },
      {
        text: "Modular Compact",
        value: WFComplicationType.ModularCompact,
      },
      {
        text: "Modular Compact Colored",
        value: WFComplicationType.ModularCompactColored,
      },
      {
        text: "Modular Duo",
        value: WFComplicationType.ModularDuo,
      },
      {
        text: "Modular Ultra",
        value: WFComplicationType.ModularUltra,
      },
    ],
  },
];

const getStatusColor = (status: WFStatus): string => {
  switch (status) {
    case WFStatus.Published:
      return 'success';
    case WFStatus.Unpublished:
      return 'default';
    case WFStatus.Banned:
      return 'error';
    case WFStatus.Suspected:
      return 'warning';
    default:
      return 'processing';
  }
};

const getCategoryTypeColor = (type: CategoryType): string => {
  switch (type) {
    case CategoryType.Admin:
      return 'purple';
    case CategoryType.Customer:
      return 'blue';
    default:
      return 'default';
  }
};

const getWatchFaceTypeLabel = (type: WatchFaceType | WFComplicationType): string => {
  if (Object.values(WFComplicationType).includes(type as WFComplicationType)) {
    const complicationType = type as WFComplicationType;
    return complicationType
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  }
  
  const watchFaceType = type as WatchFaceType;
  switch (watchFaceType) {
    case WatchFaceType.Photo:
      return 'Photo';
    case WatchFaceType.Portrait:
      return 'Portrait';
    case WatchFaceType.Animated:
      return 'Animated';
    case WatchFaceType.Complication:
      return 'Complication';
    case WatchFaceType.Parmesan:
      return 'Parmesan';
    case WatchFaceType.EdgeToEdge:
      return 'Full Screen';
    default:
      return String(type);
  }
};

const WatchFacesPage: React.FC = () => {
  const { Column } = Table;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedWatchFace, setSelectedWatchFace] =
    useState<WatchFace | null>();

  const handleAddWatchFace = () => {
    setSelectedWatchFace(null);
    setIsModalOpen(true);
  };

  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(100);
  const [total, setTotal] = useState<number>(0);
  const [category, setCategory] = useState<any>();
  const [categoryType, setCategoryType] = useState<any>(CategoryType.Admin);
  const [type, setType] = useState<WatchFaceType | undefined>();
  const [complicationType, setComplicationType] = useState<
    WFComplicationType | undefined
  >();
  const [status, setStatus] = useState<any>(WFStatus.Published);
  const [searchValue, setSearchValue] = useState<string>("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState<string>("");

  const [dataSource, setDataSource] = useState<DataType[]>([]);

  const debouncedSearch = useDebounce((value: string) => {
    setDebouncedSearchValue(value);
    setPage(1); // Reset to first page when searching
  }, 500);

  const { data: watchFaces, isLoading } = useGetWatchFaces({
    limit: limit,
    page: page,
    category: category,
    categoryType: categoryType,
    status: status,
    type: type,
    complicationType: complicationType,
    searchValue: debouncedSearchValue || undefined,
  });

  const { data: categories } = useGetCategories({
    limit: 100,
    page: 1,
    type: categoryType,
  });

  const { mutateAsync: deleteWatchFace } = useDeleteWatchFace();

  useEffect(() => {
    if (!isLoading && watchFaces?.data) {
      const limit = watchFaces.limit;
      const currentPage = watchFaces.currentPage;
      setTotal(watchFaces.resultsCount);
      setDataSource(
        watchFaces.data.map((el, index) => {
          const previewMedia = getWatchfacePreviewMedia(el);
          const depthUrl =
            el.wfType === WatchFaceType.Complication
              ? el.complicationsInfo.depthUrl
              : el?.styles?.depthUrl || el?.edgeToEdgeInfo?.depthUrl;
          const ordinalPosition = index + 1 + (currentPage - 1) * limit;
          return {
            key: el.id,
            name: el.name,
            type: el.wfType,
            status: el.status,
            category: el.category.name,
            image: {
              src: previewMedia.src,
              alt: el.name,
              type: previewMedia.type,
            },
            depthUrl: depthUrl,
            tags: el.tags,
            isFree: el.isFree,
            isApproved: el.isApproved,
            installsCount: el.installsCount,
            likesCount: el.likesCount,
            reportsCount: el.reportsCount,
            styles: el.styles || el.complicationsInfo || el.edgeToEdgeInfo,
            position: el.position,
            ordinalPosition: ordinalPosition,
            categoryType: el.category.type,
            minAppVersion: el.minAppVersion,
          };
        })
      );
    }
  }, [watchFaces]);

  return (
    <>
      <DefaultLayout>
        <main>
          <div>
            <h2 className="text-black">WatchFaces Page</h2>
            <Button onClick={handleAddWatchFace}>Add</Button>
          </div>

          <div className="mb-3" style={{ maxWidth: 400 }}>
            <Input
              placeholder="Search watchfaces..."
              value={searchValue}
              onChange={(event) => {
                setSearchValue(event.target.value);
                debouncedSearch(event.target.value);
              }}
              allowClear
              prefix={<SearchOutlined />}
              size="large"
            />
          </div>

          <div>
            <Table
              dataSource={dataSource}
              loading={isLoading}
              pagination={{
                current: page,
                total: total,
                pageSize: limit,
                position: ["bottomCenter", "topCenter"],
              }}
              onChange={(page, filter) => {
                const type = filter.type?.[0] as
                  | WFComplicationType
                  | WatchFaceType;
                setCategory(
                  filter.category?.[0] ? filter.category[0] : undefined
                );
                setCategoryType(
                  filter.categoryType?.[0] ? filter.categoryType[0] : undefined
                );
                setStatus(filter.status?.[0] ? filter.status[0] : undefined);

                if (
                  type &&
                  Object.values(WFComplicationType).includes(
                    type as WFComplicationType
                  )
                ) {
                  setComplicationType(type as WFComplicationType);
                  setType(undefined);
                } else if (
                  type &&
                  Object.values(WatchFaceType).includes(type as WatchFaceType)
                ) {
                  setType(type as WatchFaceType);
                  setComplicationType(undefined);
                } else {
                  setType(undefined);
                  setComplicationType(undefined);
                }

                page.current && setPage(page.current);
                page.pageSize && setLimit(page.pageSize);
              }}
              onRow={(record, rowIndex) => {
                return {
                  onClick: (event) => {
                    const wf = watchFaces?.data.find(
                      (w) => w.id === record.key
                    );
                    setSelectedWatchFace(wf);
                    setIsModalOpen(true);
                  },
                };
              }}
              expandable={{
                expandedRowRender: (record) => (
                  <div className="flex flex-col">
                    <span>ID: {record.key}</span>
                    <span>
                      Min App Version: {record.minAppVersion.join(".")}
                    </span>
                    <span>Status: {record.status}</span>
                    <span>Installs: {record.installsCount}</span>
                    <span>Likes: {record.likesCount}</span>
                    <span>Reports: {record.reportsCount}</span>
                    <span>
                      Styles:{" "}
                      <pre>
                        <code>{JSON.stringify(record.styles, null, 2)}</code>
                      </pre>
                    </span>
                  </div>
                ),
              }}
            >
              <Column
                title="Image"
                dataIndex="image"
                key="image"
                render={(image: { src: string }, record: DataType) => (
                  <WatchFacePreview
                    previewURL={image.src}
                    depthURL={record?.depthUrl}
                  />
                )}
              />
              {category && (
                <Column
                  title="Position"
                  dataIndex="ordinalPosition"
                  key="position"
                  width={"10%"}
                  render={(_: any, record: DataType) => (
                    <div className={styles.positionContainer}>
                      <div className={styles.positionBadge}>
                        #{record.ordinalPosition}
                      </div>
                      <div className={styles.positionValue}>
                        Pos: {record.position}
                      </div>
                    </div>
                  )}
                />
              )}
              <Column title="Name" dataIndex="name" key="name" />
              <Column
                title="Type"
                dataIndex="type"
                key="type"
                filters={watchFaceTypeFilters}
                filterMultiple={false}
                filterMode="tree"
                render={(type: string) => (
                  <Tag color="cyan" style={{ fontWeight: 500 }}>
                    {getWatchFaceTypeLabel(type as WatchFaceType | WFComplicationType)}
                  </Tag>
                )}
              />
              <Column
                title="Category"
                dataIndex="category"
                key="category"
                filters={categories?.data?.map((category) => ({
                  text: category.name,
                  value: category.id,
                }))}
                filterMultiple={false}
                filterSearch
                render={(text: string) => (
                  <Text strong style={{ color: '#1a202c' }}>
                    {text}
                  </Text>
                )}
              />
              <Column
                title="Category Type"
                dataIndex="categoryType"
                key="categoryType"
                defaultFilteredValue={[CategoryType.Admin]}
                filters={[
                  {
                    text: "All",
                    value: CategoryType.All,
                  },
                  {
                    text: "Admin",
                    value: CategoryType.Admin,
                  },
                  {
                    text: "Customer",
                    value: CategoryType.Customer,
                  },
                ]}
                filterMultiple={false}
                render={(type: CategoryType) => (
                  <Tag color={getCategoryTypeColor(type)} style={{ fontWeight: 500 }}>
                    {type === CategoryType.Admin ? 'Admin' : type === CategoryType.Customer ? 'Customer' : 'All'}
                  </Tag>
                )}
              />
              <Column
                title="Tags"
                dataIndex="tags"
                key="tags"
                width={"15%"}
                render={(tags: string[]) => (
                  <div className="flex flex-wrap gap-2">
                    {tags.map((tag) => (
                      <Tag className="m-0" color="blue" key={tag}>
                        {tag}
                      </Tag>
                    ))}
                  </div>
                )}
              />
              <Column
                title="Free"
                dataIndex="isFree"
                key="isFree"
                align={"center"}
                render={(isFree: boolean) => {
                  return (
                    <>
                      {isFree ? (
                        <CheckCircleOutlined />
                      ) : (
                        <CloseCircleOutlined />
                      )}
                    </>
                  );
                }}
              />
              <Column
                title="Status"
                dataIndex="status"
                key="status"
                defaultFilteredValue={[WFStatus.Published]}
                filters={[
                  {
                    text: "Published",
                    value: WFStatus.Published,
                  },
                  {
                    text: "Unpublished",
                    value: WFStatus.Unpublished,
                  },
                  {
                    text: "Banned",
                    value: WFStatus.Banned,
                  },
                  {
                    text: "Suspected",
                    value: WFStatus.Suspected,
                  },
                ]}
                filterMultiple={false}
                render={(status: WFStatus) => {
                  const statusLabels: Record<WFStatus, string> = {
                    [WFStatus.Published]: 'Published',
                    [WFStatus.Unpublished]: 'Unpublished',
                    [WFStatus.Banned]: 'Banned',
                    [WFStatus.Suspected]: 'Suspected',
                  };
                  return (
                    <Tag color={getStatusColor(status)} style={{ fontWeight: 500 }}>
                      {statusLabels[status]}
                    </Tag>
                  );
                }}
              />
              <Column
                title="Action"
                key="action"
                render={(_: any, record: DataType) => (
                  <Space size="middle">
                    <AntButton
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        const wf = watchFaces?.data.find(
                          (w) => w.id === record.key
                        );
                        setSelectedWatchFace(wf);
                        setIsDeleteModalOpen(true);
                      }}
                    >
                      Delete{" "}
                    </AntButton>
                  </Space>
                )}
              />
            </Table>
          </div>
        </main>
      </DefaultLayout>
      <CreateWatchFaceModal
        setIsOpen={setIsModalOpen}
        isOpen={isModalOpen}
        watchface={selectedWatchFace}
        defaultCategoryId={category}
      />
      <Modal
        title="Delete WatchFace"
        content={"Are you sure you want to delete this WatchFace?"}
        open={isDeleteModalOpen}
        onCancel={() => {
          setIsDeleteModalOpen(false);
        }}
        cancelText={"No, keep the WatchFace"}
        onOk={async () => {
          selectedWatchFace?.id &&
            (await deleteWatchFace(selectedWatchFace.id));
          setIsDeleteModalOpen(false);
          setSelectedWatchFace(null);
        }}
        okText={"Yes, delete it"}
      />
    </>
  );
};

export default WatchFacesPage;
